import React, { useEffect, useState } from 'react';
import { ssoApi } from '../lib/api';

/**
 * "Continue with Google" / "Continue with Microsoft".
 *
 * ⚠️ THE SAME WORDING ON BOTH SCREENS, deliberately (Rob, 16/08/2026). "Sign up with" on
 * one and "Sign in with" on the other reads better in isolation but is a promise we cannot
 * keep: the button does not know which it is until the provider tells us whether that email
 * address already has an account. "Continue with" is true either way.
 *
 * ⚠️ NOTHING IS DRAWN UNLESS THE BACKEND SAYS THE PROVIDER IS CONFIGURED. Blank credentials
 * are a normal state — local development, a fresh checkout, a staging box whose .env hasn't
 * been filled in — and a button that opens a 503 is worse than no button.
 */
const PROVIDER_STYLE = {
  google: { label: 'Google' },
  microsoft: { label: 'Microsoft' },
};

const GoogleMark = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.5 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1C3.4 21.3 7.4 24 12 24z" />
    <path fill="#FBBC05" d="M5.4 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.6.4-2.4V6.5H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.5l4-3.1z" />
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.7 1.4 6.5l4 3.1C6.3 6.8 8.9 4.8 12 4.8z" />
  </svg>
);

const MicrosoftMark = () => (
  <svg className="w-5 h-5" viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
);

const MARKS = { google: GoogleMark, microsoft: MicrosoftMark };

const SsoButtons = () => {
  const [available, setAvailable] = useState([]);

  useEffect(() => {
    let cancelled = false;
    ssoApi.providers()
      .then((r) => {
        if (cancelled) return;
        setAvailable(Object.entries(r?.data || {})
          .filter(([, spec]) => spec?.configured)
          .map(([name]) => name));
      })
      .catch(() => {});   // no buttons is a perfectly good outcome
    return () => { cancelled = true; };
  }, []);

  if (!available.length) return null;

  return (
    <div className="mt-6" data-testid="sso-buttons">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-border" />
        <span className="text-sm text-muted-foreground">or</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="space-y-3">
        {available.map((provider) => {
          const Mark = MARKS[provider];
          const label = PROVIDER_STYLE[provider]?.label || provider;
          return (
            <a
              key={provider}
              /* ⚠️ A real navigation, not a fetch — the provider has to redirect the
                 browser away and back. An XHR here fails with a CORS error that looks
                 like our bug. */
              href={ssoApi.startUrl(provider)}
              className="w-full h-11 rounded-md border border-border bg-card hover:bg-muted
                         flex items-center justify-center gap-3 text-sm font-medium transition-colors"
              data-testid={`sso-${provider}`}
            >
              {Mark ? <Mark /> : null}
              Continue with {label}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default SsoButtons;
